import {
  Circle,
  GoogleMap,
  LoadScript,
  Marker,
  MarkerClusterer,
  MarkerF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { DateTime } from "luxon";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CalculateDefaultValue,
  calculateParamsProps,
  CalculateProps,
  FacilityTypes,
  GeocodeResultProps,
} from "../../../helpers/types/getTypes";
import { client } from "../../../utils/axios";
import { GpsIcon, MinusIcon, PlusIcon } from "../../commons/icon/SvgIcon";
import { LoadingMap } from "../../commons/loading/Skeleton";
import AutoCompleteMap from "./AutoCompleteMap";

import {
  boundariesUSA,
  createRadius,
  getDynamicResult,
  getResultCity,
  getSpesificResultGeocode,
  LIBRARIES,
  LOADER,
  MAP_DEFAULT,
  MAP_ID,
  MAP_USA,
  METER,
  OPTIONS,
  sleep,
  USA_CENTER,
  VERSION,
} from "./helper";
import RadiusWaypoints from "./RadiusWaypoints";

export const defaultValueCalculate = {
  lat: MAP_DEFAULT.lat.toString(),
  lng: MAP_DEFAULT.lng.toString(),
  radius: null,
  map_zoom: 11,
  times: "",
} as calculateParamsProps;

const Map = ({
  mode,
  onChange,
  setTotal,
  onLoading,
  getCity,
}: {
  mode: string;
  onChange?: (e: any, v: any) => void;
  setTotal?: (e: number) => void;
  onLoading?: (e: boolean) => void;
  getCity?: (e: string) => void;
}) => {
  const router = useRouter();
  const { isLoaded, loadError } = useJsApiLoader(LOADER as any);
  const [loading, setLoading] = useState(true);
  const [loadingCluster, setLoadingCluster] = useState(true);
  const [defaultCenter, setDefaultCenter] = useState(MAP_DEFAULT);

  const [center, setCenter] = useState(MAP_DEFAULT);

  const [zoom, setZoom] = useState<{ default: number; min: number }>({
    default: 11,
    min: 4,
  });

  const [wayvalue, setWayvalue] = useState<number | undefined>();
  const [visible, setVisible] = useState(true);
  const [radius, setRadius] = useState<{
    miles: number;
    meter: number;
    min: number;
    max: number;
    mobile: number;
    waypoints: number[];
  }>({
    miles: 0,
    meter: 0,
    min: 0,
    max: 500,
    mobile: 0,
    waypoints: [],
  });

  const [city, setCity] = useState<string>("");
  const [searchShow, setSearchShow] = useState("");
  const [disabled, setDisabled] = useState(false);

  const [items, setItems] = useState<{
    status: string;
    data: CalculateProps;
    times: string;
  }>({
    status: "",
    data: CalculateDefaultValue,
    times: "",
  });

  const [params, setParams] = useState<calculateParamsProps>({
    lat: "",
    lng: "",
    radius: "",
    times: DateTime.now().toISO(),
    total: 0,
    map_zoom: 11,
  });

  const [lastParams, setLastParams] = useState<{
    lat: string;
    lng: string;
  }>({
    lat: "",
    lng: "",
  });

  const [type, setType] = useState("");
  const [delay, setDelay] = useState(false);
  const [placeID, setPlaceID] = useState<GeocodeResultProps | null>(null);

  const [paramsQuery, setParamsQuery] = useState<calculateParamsProps>(
    defaultValueCalculate
  );

  const [typeFac, setTypeFac] = useState("");
  const [facility, setFacility] = useState<FacilityTypes[]>([]);
  const [totalFac, setTotalFac] = useState(0);
  const clustererRef = useRef<any>();

  const [map, setMap] = useState<any>(null);

  const onLoad = useCallback(
    function callback(map: any) {
      setMap(map);
    },
    [center]
  );

  const onUnmount = useCallback(function callback(map: any) {
    setMap(null);
  }, []);

  // ONBOUND CIRCLE
  const onBoundCircle = (map: any, event = "idle", local = null) => {
    let zoomLevel = 11;
    const miles = radius.miles > 0 ? radius.miles : 50;
    const numRad = local ? local : miles;
    const getMeter = numRad * METER;
    const getRadius = getMeter + getMeter / 2;
    const scale = getRadius / 500;

    let x = 15;
    if (mode === "desktop") {
      x = 16.5;
    }

    zoomLevel = x - Math.log(scale) / Math.log(2);
    if (radius.miles > 0 && radius.miles === radius.max) {
      zoomLevel = 4;
    }

    if (event === "radius_changed") {
      setZoom({ ...zoom, default: zoomLevel });
    } else {
      const listener = window.google.maps.event.addListener(
        map,
        event,
        function () {
          setZoom({ ...zoom, default: zoomLevel });

          window.google.maps.event.clearListeners(map, "idle");
        }
      );
    }
  };

  // GET CURRENT LOCATION
  const currentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        getAddress("current", pos.lat, pos.lng);
        setCenter(pos);
        setDefaultCenter(pos);

        setParamsQuery({
          ...paramsQuery,
          lat: pos.lat.toString(),
          lng: pos.lng.toString(),
        });
        if (map) {
          sleep(1000).then(() => {
            onBoundCircle(map, "radius_changed");
          });
        }
      },
      () => {
        setCenter(MAP_DEFAULT);
        setDefaultCenter(MAP_DEFAULT);

        getAddress("current", MAP_DEFAULT.lat, MAP_DEFAULT.lng);
        setParamsQuery({
          ...paramsQuery,
          lat: MAP_DEFAULT.lat.toString(),
          lng: MAP_DEFAULT.lng.toString(),
        });
        if (map) {
          sleep(1000).then(() => {
            onBoundCircle(map, "radius_changed");
          });
        }
      }
    );
  };

  // GET CHANGE LOCATION
  const changeLocation = (place_id: string, desc: any) => {
    const geocode = new window.google.maps.Geocoder();
    geocode
      .geocode({
        placeId: place_id,
      })
      .then(({ results }) => {
        const pos = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };
        setPlaceID(results[0]);
        setCity(results[0].formatted_address);
        getCity?.(results[0].formatted_address);
        setCenter(pos);
        setDefaultCenter(pos);
        setParamsQuery({
          ...paramsQuery,
          lat: pos.lat.toString(),
          lng: pos.lng.toString(),
        });
        if (map) {
          sleep(1000).then(() => {
            onBoundCircle(map, "radius_changed");
          });
        }
      });
  };

  const getAddress = (type = "", lat?: any, lng?: any) => {
    let latlng = map.getCenter();
    if (type) {
      latlng = new google.maps.LatLng(lat, lng);
    }
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      {
        location: latlng,
        region: "us",
      },
      (results, status) => {
        if (status == google.maps.GeocoderStatus.OK) {
          if (results) {
            const res = getSpesificResultGeocode(results, "street_address");
            setPlaceID(res);
            setCity(res.formatted_address);
            getCity?.(res.formatted_address);
          }
        }
      }
    );
  };

  //   GET API
  const getAPI = async () => {
    if (delay) {
      return false;
    }

    setFacility([]);
    setTotalFac(0);
    setDelay(true);
    setLoading(true);
    onLoading?.(true);
    const data = {
      ...paramsQuery,
    };
    setParams({ ...paramsQuery, times: DateTime.now().toISO() });
    await client
      .post("/m/pricing-plan", data)
      .then((response) => {
        setItems({
          status: "OK",
          data: response.data,
          times: DateTime.now().toISO(),
        });

        setTypeFac(response.data.type);
        setTimeout(() => {
          if (response.data.found) {
            setFacility(response.data.found.facilities);
            let num = 0;
            response.data.found.facilities.forEach(
              (v: FacilityTypes) =>
                (num = num + (response.data.type !== "area" ? v.total || 0 : 1))
            );
            setTotalFac(num);
            setTotal?.(num);
          }
        }, 100);

        const localRadius = localStorage.getItem("radius") || "";
        if (!localRadius) {
          localStorage.setItem("radius", JSON.stringify(response.data.radius));
          const date: any = DateTime.now().plus({ days: 1 }).toISO();
          localStorage.setItem("time-radius", date);
        }

        if (radius.miles <= 0) {
          const localRadius = localStorage.getItem("radius") || "";
          if (localRadius) {
            const rad = JSON.parse(localRadius);
            setRadius({
              miles: rad.default,
              meter: rad.default * METER,
              min: rad.waypoints[0],
              max: rad.waypoints[rad.waypoints.length - 1],
              mobile: rad.default,
              waypoints: rad.waypoints,
            });
          } else {
            setRadius({
              miles:
                response.data.radius.default > 0
                  ? response.data.radius.default
                  : 50,
              meter:
                (response.data.radius.default > 0
                  ? response.data.radius.default
                  : 50) * METER,
              min: response.data.radius.waypoints[0],
              max: response.data.radius.waypoints[
                response.data.radius.waypoints.length - 1
              ],
              mobile:
                response.data.radius.default > 0
                  ? response.data.radius.default
                  : 50,
              waypoints: response.data.radius.waypoints,
            });
          }
          setParams({
            ...params,
            radius:
              response.data.radius.default > 0
                ? response.data.radius.default
                : 50,
          });
        }
      })
      .catch((err: any) => {
        setItems({
          data: CalculateDefaultValue,
          status: "NOTFOUND",
          times: DateTime.now().toISO(),
        });
        setTotal?.(0);

        if (radius.miles <= 0) {
          const localRadius = localStorage.getItem("radius") || "";
          if (localRadius) {
            const rad = JSON.parse(localRadius);
            setRadius({
              miles: rad.default,
              meter: rad.default * METER,
              min: rad.waypoints[0],
              max: rad.waypoints[rad.waypoints.length - 1],
              mobile: rad.default,
              waypoints: rad.waypoints,
            });
          } else {
            const num = 15;
            const way = [15, 500];
            setRadius({
              miles: num,
              meter: num * METER,
              min: way[0],
              max: way[way.length - 1],
              mobile: num,
              waypoints: way,
            });
          }
        }

        if (
          err?.response &&
          err?.response?.data?.message === "Sorry failed to get address detail"
        ) {
          setCity("Unknown location");
        }
      })
      .finally(async () => {
        setLoadingCluster(false);
        clustererRef.current?.repaint();
        await sleep(1000).then(() => {
          setLoading(false);
          onLoading?.(false);
          setDelay(false);
        });
      });
  };

  const setFilterMapBound = (
    map: any,
    isParams = true,
    addParams: calculateParamsProps | null = null
  ) => {
    if (!map) {
      return null;
    }

    const boundZoom = map.getZoom();
    const mapCenter = map.getCenter();
    const pos = {
      lat: mapCenter.lat(),
      lng: mapCenter.lng(),
    };
    // zoom level below 6 must display whole country
    if (addParams) {
      addParams = {
        ...addParams,
      };
    }

    let zoomMap = {
      map_zoom:
        addParams && addParams.map_zoom
          ? addParams.map_zoom
          : Math.ceil(boundZoom),
    };

    if (isParams) {
      setParamsQuery({
        ...paramsQuery,
        ...(addParams ? { ...addParams, ...zoomMap } : zoomMap),
        // lat: pos.lat.toString(),
        // lng: pos.lng.toString(),
        times: DateTime.now().toISO(),
        radius: wayvalue as any,
      });
    }
  };

  // change radius
  useEffect(() => {
    if (wayvalue) {
      if (wayvalue === radius.max) {
        const featureLayer = map.getFeatureLayer("COUNTRY");
        featureLayer.style = (options: any) => {
          if (options.feature.placeId === MAP_USA) {
            return boundariesUSA;
          }
        };
        setDefaultCenter(USA_CENTER);
        setCenter(USA_CENTER);
        setLastParams({
          lat: paramsQuery.lat,
          lng: paramsQuery.lng,
        });
        setParamsQuery({
          ...paramsQuery,
          radius: wayvalue,
          times: "",
        });
        setZoom({ ...zoom, default: mode === "desktop" ? 4 : 2 });
        setVisible(false);
        setDisabled(true);
        setType("full");
      } else {
        const featureLayer = map.getFeatureLayer("COUNTRY");
        featureLayer.style = null;
        setVisible(true);
        setDisabled(false);
        setType("partial");
        if (!lastParams.lat && !lastParams.lng) {
          setParamsQuery({
            ...paramsQuery,
            radius: wayvalue,
            times: "",
          });
          onBoundCircle(map, "radius_changed");
        }
      }
    }
  }, [wayvalue]);

  useEffect(() => {
    if (items.times && params.times) {
      onChange?.(items, params);
    }
  }, [items]);

  useEffect(() => {
    if (type === "partial" && lastParams.lat && lastParams.lng) {
      const lat = lastParams.lat;
      const lng = lastParams.lng;
      setDefaultCenter({
        lat: Number(lat),
        lng: Number(lng),
      });
      setCenter({
        lat: Number(lat),
        lng: Number(lng),
      });
      if (map) {
        sleep(1000).then(() => {
          onBoundCircle(map, "radius_changed");
          setLastParams({
            lat: "",
            lng: "",
          });
        });
      }
    }
  }, [type]);

  useEffect(() => {
    const date = DateTime.now();
    const localDate = localStorage.getItem("time-radius") || "";
    if (localDate) {
      const setDate = DateTime.fromISO(localDate);
      if (date.toString() > setDate.toString()) {
        localStorage.removeItem("radius");
      }
    }
  }, []);

  useEffect(() => {
    if (placeID) {
      const data = {
        state: getDynamicResult(placeID, "administrative_area_level_1"),
        city: getDynamicResult(placeID, "locality"),
        zipcode: getDynamicResult(placeID, "postal_code"),
        address: placeID.formatted_address,
        radius: paramsQuery.radius || 50,
        price_monthly: items.data.pricing.monthly,
        price_yearly: items.data.pricing.yearly,
      };

      // @ts-ignore
      analytics.ready(function () {
        // @ts-ignore
        analytics.reset();
        // @ts-ignore
        analytics.track(`pricing_plan`, data);
      });
    }
  }, [placeID, items]);

  useEffect(() => {
    if (paramsQuery.times) {
      getAPI();
    }
  }, [paramsQuery]);

  useEffect(() => {
    if (map) {
      getAddress();
      sleep(1000).then(() => {
        onBoundCircle(map, "radius_changed");
      });
    }
  }, [map]);

  return (
    <>
      {isLoaded ? (
        <GoogleMap
          id="map_sual"
          mapContainerClassName="w-full h-full rounded-[0.875rem] flex flex-col p-4.5"
          center={defaultCenter}
          zoom={zoom.default}
          options={{ ...OPTIONS, draggable: false }}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={() => setFilterMapBound(map)}
          onZoomChanged={() => setFilterMapBound(map)}
        >
          {/* Child components, such as markers, info windows, etc. */}
          <>
            <Circle
              center={center}
              radius={radius.meter}
              options={{
                strokeColor: "#3366FF",
                strokeOpacity: 1,
                strokeWeight: 1,
                fillColor: "#3366FF",
                fillOpacity: 0.2,
                map,
                zIndex: 1,
              }}
              visible={visible}
            />

            {radius.miles < radius.max && (
              <Marker
                icon="/marker.svg"
                position={center}
                // options={{
                //   map,
                // }}
              />
            )}

            {!loadingCluster && typeFac && (
              <MarkerClusterer
                styles={
                  typeFac === "area"
                    ? [
                        {
                          textColor: "white",
                          url: "/images/map/cluster/m1.svg",
                          height: 60,
                          width: 60,
                        },
                      ]
                    : typeFac === "city"
                    ? [
                        {
                          textColor: "white",
                          url: "/images/map/cluster/mc1.svg",
                          height: 60,
                          width: 60,
                        },
                      ]
                    : typeFac === "state"
                    ? [
                        {
                          textColor: "white",
                          url: "/images/map/cluster/ms1.svg",
                          height: 60,
                          width: 60,
                        },
                      ]
                    : []
                }
                onLoad={(clusterer) => (clustererRef.current = clusterer)}
                onClick={(clusterer) => {
                  const pos = {
                    lat: clusterer.getCenter()?.lat(),
                    lng: clusterer.getCenter()?.lng(),
                  };
                  // @ts-ignore
                  setDefaultCenter(pos);
                  // @ts-ignore
                  setCenter(pos);
                }}
                zoomOnClick
              >
                {(clusterer) => {
                  clusterer.redraw();
                  clusterer.repaint();
                  return (
                    <>
                      {facility.map((e, key: number) => (
                        <Marker
                          key={key}
                          icon={
                            typeFac === "area"
                              ? "/images/map/icon/pin-marker.svg"
                              : typeFac === "city"
                              ? "/images/map/icon/pin-marker-cities.svg"
                              : "/images/map/icon/pin-marker-states.svg"
                          }
                          position={{
                            lat: e.lat,
                            lng: e.lng,
                          }}
                          clusterer={clusterer}
                          zIndex={50}
                          onClick={() => {
                            if (typeFac !== "area") {
                              const pos = { lat: e.lat, lng: e.lng };
                              setDefaultCenter(pos);
                              setCenter(pos);

                              sleep(1000).then(() => {
                                setZoom({
                                  ...zoom,
                                  default: 11,
                                });
                              });
                            }
                          }}
                        />
                      ))}
                    </>
                  );
                }}
              </MarkerClusterer>
            )}

            <div className="relative">
              <div className="flex flex-row justify-between w-full ">
                {/* AUTOCOMPLETE */}
                {searchShow && (
                  <div
                    className={`fixed inset-0 z-10 transition-opacity bg-transparent opacity-50 ${
                      searchShow === "close" && "hidden"
                    }`}
                    onClick={() => {
                      setSearchShow("close");
                    }}
                  />
                )}

                <div className="w-full absolute left-0 z-10">
                  {map && (
                    <AutoCompleteMap
                      label="Search location..."
                      changeLocation={changeLocation}
                      currentLocation={currentLocation}
                      searchShow={(e) => setSearchShow(e)}
                      show={searchShow}
                      searchResult={city}
                      disabled={disabled}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="relative mt-auto">
              {/* MAP CONFIG */}
              <div className="absolute right-2 bottom-20 flex flex-col space-y-2">
                <div className="w-auto shadow-lv3 flex flex-col bg-transparent rounded-xl">
                  <div
                    className="p-3 bg-white rounded-xl hover:bg-neutral-200 cursor-pointer transition ease-in-out duration-200"
                    onClick={currentLocation}
                  >
                    <GpsIcon />
                  </div>
                </div>
                <div className="w-auto shadow-lv3 flex flex-col bg-transparent rounded-xl">
                  <div
                    className="p-3 pb-1.5 bg-white rounded-t-xl hover:bg-neutral-200 cursor-pointer transition ease-in-out duration-200"
                    onClick={() => {
                      const res = (map?.getZoom() || 0) + 1;
                      setZoom({ ...zoom, default: res });
                    }}
                  >
                    <PlusIcon />
                  </div>
                  <div
                    className="p-3 pt-1.5 bg-white rounded-b-xl hover:bg-neutral-200 cursor-pointer transition ease-in-out duration-200"
                    onClick={() => {
                      const res = (map?.getZoom() || 0) - 1;
                      setZoom({ ...zoom, default: res });
                    }}
                  >
                    <MinusIcon />
                  </div>
                </div>
              </div>

              {/* RADIUS GATEWAY */}
              <RadiusWaypoints
                className="hidden sm:hidden md:block lg:block"
                radius={radius}
                onAfterChange={(e) => {
                  setWayvalue(e);
                  setRadius({
                    ...radius,
                    meter: e * METER,
                    miles: e,
                  });
                }}
                onChange={(e) => {
                  setRadius({
                    ...radius,
                    meter: e * METER,
                    miles: e,
                  });
                }}
                marks={createRadius(radius.waypoints)}
              />

              <div className="group relative lg:hidden md:hidden">
                <label className="bg-white flex flex-row p-2 px-5 w-full shadow-lv3 rounded-xl relative">
                  <span className="w-1/3 text-neutral-600">Pick Radius</span>
                  <select
                    className="text-right px-4 w-2/3"
                    onChange={(e) => {
                      setWayvalue(parseInt(e.target.value));
                      setRadius({
                        ...radius,
                        meter: parseInt(e.target.value) * METER,
                        miles: parseInt(e.target.value),
                      });
                    }}
                  >
                    {radius.waypoints.map((item, key) => (
                      <option value={item} key={key}>
                        {item} Miles
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </>
        </GoogleMap>
      ) : (
        <LoadingMap />
      )}

      {loadError && (
        <div className="p-6 text-neutral-900">
          Map cannot be loaded right now, sorry.
        </div>
      )}
    </>
  );
};

export default Map;
